import {escapeHTML} from "../lib/escapeHTML";
import {bootstrapModal} from "../lib/bootstrapModal";
import {Messages} from "../messages/Messages";
import {toggleDisabled, toggleHidden} from "../lib/domFunctions";
import {fetchJsonForApiResponse, isAjaxResponseOk, toastFetchError} from "../lib/fetch";
import {AppConfig} from "../AppConfig";
import {apiPost} from "../lib/SecuredAjax";
import {showToastInfo} from "../lib/boostrapToast";
import {PositionSelectOption} from "../dto/com.rico.sb2.service.positions";
import {springNumber} from "../lib/springForms";
import {SchemaState} from "../SchemaState";
import {ProcessControlService_Mode} from "../dto/com.rico.sb2.service";
import {ContainerDataAdapter} from "../dashboard/ContainerDataSupplier";
import {coalesce, nullWhenNaN} from "../lib/coalesce";

const messages = new Messages();

interface SendContainerToPositionParameters {
	currentPosition?: number | null,
	target?: number | null,
	runoffTime?: number | null,
	nextActionPosition?: number | null,
	schemaState?: SchemaState | null,
	success?: Function
}

export class SendContainerToPosition {
	private readonly currentPosition: number | null
	private readonly nextActionPosition: number | null
	private readonly schemaState: SchemaState | null
	private readonly container: number

	private readonly target: number | null
	private readonly runoffTime: number | null

	constructor(container: number, options: SendContainerToPositionParameters) {
		this.currentPosition = nullWhenNaN(options.currentPosition);
		this.container = container
		this.schemaState = coalesce(options.schemaState, null);
		this.nextActionPosition = nullWhenNaN(options.nextActionPosition)
		this.target = nullWhenNaN(options.target);
		this.runoffTime = nullWhenNaN(options.runoffTime);
	}

	static show(container: number, options: SendContainerToPositionParameters) {
		new SendContainerToPosition(container, options).showModal(options.success)
	}

	static showForContainerDataAdapter(containerData: ContainerDataAdapter, schemaState: SchemaState | null, options: SendContainerToPositionParameters = {}) {
		const finalOptions: SendContainerToPositionParameters = {
			currentPosition: containerData.position,
			nextActionPosition: containerData.stepNextPosition,
			runoffTime: containerData.stepRunoffTime,
			schemaState: schemaState,
			...options
		}
		new SendContainerToPosition(containerData.id, finalOptions).showModal(finalOptions.success)
	}

	static fetchMoveTargets(container: number): Promise<PositionSelectOption[]> {
		return fetchJsonForApiResponse(`${AppConfig.CP}/op/listMoveContainerTargets?container=${container}`)
	}

	static execute(container: number, targetPosition: number, runoffTime: number = 0) {
		new SendContainerToPosition(container, {}).execute(targetPosition, runoffTime)
	}

	execute(position: number, runoffTime: number = 0): Promise<void> {
		return new Promise((resolve, reject) => {
			apiPost(`${AppConfig.CP}/op/moveContainer`, {container: this.container, position, runoffTime})
				.then(res => isAjaxResponseOk(res) ? res.value : Promise.reject(res.message))
				.then(success => {
					if (!success) return Promise.reject(500)

					showToastInfo(messages.get('SendContainerToPosition.completeNotice'))
					resolve()
				})
				.catch(error => {
					toastFetchError(error);
					reject();
				})
		})
	}

	showModal(success?: Function) {
		const {modal, content, buttonOk, buttonOkClickStart, buttonOkClickFinish} = bootstrapModal({
			title: messages.get('SendContainerToPosition.title', this.container),
			body: this.renderForm(),
			buttonOk: messages.get('button.send')
		});
		modal.show();

		const ok = buttonOk!!;
		const positionSelect = content.querySelector<HTMLSelectElement>('[name="position"]')!!;
		const runoffTimeSelect = content.querySelector<HTMLSelectElement>('[name="runoffTime"]');

		function okToggle() {
			const positionId = parseInt(positionSelect.value);
			const positionSelected = isFinite(positionId);
			toggleDisabled(ok, !positionSelected)
		}

		positionSelect.addEventListener('change', okToggle);
		okToggle()

		ok.addEventListener('click', () => {
			const position = parseInt(positionSelect.value);
			if (!position) return;

			const runoffTime = runoffTimeSelect ? parseInt(runoffTimeSelect.value) : 0;

			buttonOkClickStart();

			this.execute(position, runoffTime)
				.then(() => {
					modal.hide()
					if (success) success()
				})
				.catch(buttonOkClickFinish)
		})

		function loadingStop() {
			content.querySelector('[data-bind="loading"]')?.remove()
			okToggle()
		}

		SendContainerToPosition.fetchMoveTargets(this.container)
			.then((positions: PositionSelectOption[]) => {
				positions = positions.filter((p: any) => p.id != this.currentPosition)

				if (positions.length > 0) {
					positionSelect.innerHTML = positions
						.map((p: any) => {
							const value = `${p.id}`
							let selected = '';
							let label = escapeHTML(p.label);
							let className = '';

							if (p.id == this.nextActionPosition) {
								label += messages.get('SendContainerToPosition.nextActionPositionSuffix')
								className = 'fw-bolder'
							}

							if (this.target != null) {
								selected = p.id == this.target ? ' selected ' : '';
							} else if (p.id == this.nextActionPosition) {
								selected = ' selected ';
							}

							return `<option value="${value}" class="${className}" ${selected}>${escapeHTML(p.code)}: ${label}</option>`;
						})
						.join("")
					toggleHidden(content.querySelector('[data-bind="select"]')!!, false)
				} else {
					toggleHidden(content.querySelector('[data-bind="emptyMessage"]')!!, false)
				}
			})
			.catch(toastFetchError)
			.then(loadingStop)
	}

	private renderForm(): string {
		const positionForRunOff = this.currentPosition && this.schemaState != null
			&& !this.schemaState.isLoadingPosition(this.currentPosition)
			&& !this.schemaState.isUnloadingPosition(this.currentPosition)

		const runoffTimeSelect = springNumber({name: `runoffTime`, labelCode: 'processAction.form.runoffTime', value: coalesce(this.runoffTime, 0), min: 0});
		const runoffTimeSection = positionForRunOff && this.schemaState?.mode == ProcessControlService_Mode.SEMIAUTOMATIC ? `<div>${runoffTimeSelect}</div>` : '';

		return `
            <div class="text-center" data-bind="loading"><div class="lds-dual-ring"></div></div>        
            <div class="hidden text-center" data-bind="emptyMessage">${escapeHTML(messages.get('SendContainerToPosition.noAvailablePositions'))}</div>
            <div class="hidden" data-bind="select">
            	<div class="mb-3">
						<select name="position" class="form-select" required></select>
				</div>
            	${runoffTimeSection}
            </div>
        `
	}
}